/*
 * Book page behaviour. The only per-section script in the theme, and
 * deliberately not motion: motion stays declarative in superette-motion.js.
 * Everything here is about the moment around submitting.
 *
 * 1. Focus the confirmation, or the error summary. Shopify already returns to
 *    #SuperetteBookingForm, so scrolling is handled. This is so a screen
 *    reader lands on the outcome rather than at the top of a page that, to
 *    anyone not looking at it, appears not to have changed. A role="alert"
 *    region that was already in the document when it was parsed is never
 *    announced - live regions fire on mutation, and a post-and-render is a
 *    fresh document - so focus is the only thing that delivers it.
 *
 * 2. Put the seven custom fields back after a validation error. The form
 *    object exposes only body, email, errors, id, name, phone and
 *    posted_successfully? for a contact form, and Liquid has no params
 *    object, so the other seven genuinely cannot be repopulated server-side.
 *    (form.company exists in Shopify's docs but is exclusive to
 *    customer_address; it reads like it would work here and does not.) They
 *    go through sessionStorage instead, and only into fields that are still
 *    empty, so this never fights what Liquid has already filled.
 *
 * 3. Lock the button while the POST is in flight. This is a full page
 *    navigation, so nothing here reports progress on its own, and a second
 *    press sends a second booking request to a two-person shop. The lock is
 *    aria-disabled plus a submit guard rather than the disabled attribute,
 *    so the person keeps their focus while the page changes under them.
 *
 * 4. Mark an empty date control, which CSS cannot ask about.
 *
 * There is no honeypot. An earlier draft had one, and it was a silent
 * total-failure path: a filled hidden field killed submission with no error,
 * no disabled state and no message, and Chrome's address autofill will fill
 * an unlabelled text input in a form that already carries name, email, tel
 * and organization - which this one does. The layer that actually stops spam
 * is Shopify's own invisible bot protection on the contact form, which is an
 * admin setting rather than theme code.
 *
 * Every storage access is wrapped: private windows, blocked site data and a
 * full quota all throw, and none of them should stop the form from posting.
 */
(function () {
  'use strict';

  var form = document.getElementById('SuperetteBookingForm');
  if (!form) return;

  var KEY = 'superette:booking-draft';

  function save() {
    var fields = form.querySelectorAll('[name^="contact["]');
    var values = {};
    for (var i = 0; i < fields.length; i++) {
      if (fields[i].value) values[fields[i].name] = fields[i].value;
    }
    try {
      sessionStorage.setItem(KEY, JSON.stringify(values));
    } catch (error) {
      /* Nothing to do: the post still goes through, the retry just starts blank. */
    }
  }

  function clear() {
    try {
      sessionStorage.removeItem(KEY);
    } catch (error) {
      /* Same. */
    }
  }

  function restore() {
    if (!window.CSS || typeof window.CSS.escape !== 'function') return;

    var raw;
    try {
      raw = sessionStorage.getItem(KEY);
    } catch (error) {
      return;
    }
    if (!raw) return;

    var values;
    try {
      values = JSON.parse(raw);
    } catch (error) {
      clear();
      return;
    }

    for (var name in values) {
      if (!Object.prototype.hasOwnProperty.call(values, name)) continue;
      // The names hold [, ] and spaces, so they need escaping to survive as a
      // selector.
      var field = form.querySelector('[name=' + window.CSS.escape(name) + ']');
      if (field && !field.value) field.value = values[name];
    }
  }

  var confirmation = form.querySelector('[data-booking-confirmation]');
  var errors = form.querySelector('[role="alert"]');

  if (confirmation) {
    // The element only renders when form.posted_successfully? is true, so its
    // existence is the whole signal - no state flag needed.
    confirmation.focus();
  }

  if (errors) {
    restore();
    errors.focus();
  } else {
    // Neither pending nor failed, so there is no reason to leave a name, an
    // email address and a phone number sitting in storage for the rest of the
    // browsing session.
    clear();
  }

  /* ------------------------------------------------------- the empty date */

  var dateField = form.querySelector('input[type="date"]');
  if (dateField) {
    // Chromium paints an untouched mm/dd/yyyy in full body colour, and CSS has
    // no way to ask a date input whether it holds a value.
    var markEmpty = function () {
      dateField.classList.toggle('is-empty', dateField.value === '');
    };
    markEmpty();
    dateField.addEventListener('input', markEmpty);
    dateField.addEventListener('change', markEmpty);
  }

  /* ---------------------------------------------------------- in flight */

  var button = form.querySelector('button[type="submit"]');
  var buttonLabel = button ? button.textContent : '';
  var sending = false;

  function lock() {
    if (!button) return;
    // Pinned before the label changes, so the button does not shrink from
    // "Send request" to "Sending" underneath the pointer that just pressed
    // it. Measured rather than declared: the width belongs to this label at
    // this size, and a number in the stylesheet would be a guess that goes
    // stale the moment either changes.
    button.style.minWidth = button.getBoundingClientRect().width + 'px';
    // aria-disabled, not disabled. A disabled control is removed from the tab
    // order, and disabling the button someone has just pressed throws their
    // focus to <body> - at the exact moment the page is about to navigate and
    // they most need to keep their place. The guard below is what actually
    // stops the second submit.
    button.setAttribute('aria-disabled', 'true');
    button.setAttribute('aria-busy', 'true');
    button.classList.add('button--disabled');
    button.textContent = 'Sending…';
  }

  function unlock() {
    if (!button) return;
    sending = false;
    button.removeAttribute('aria-disabled');
    button.removeAttribute('aria-busy');
    button.classList.remove('button--disabled');
    button.textContent = buttonLabel;
  }

  form.addEventListener('submit', function (event) {
    if (sending) {
      // Keyboard Enter on a focused aria-disabled button still submits;
      // pointer-events on .button--disabled only stops the mouse.
      event.preventDefault();
      return;
    }
    sending = true;
    save();
    // Deferred by a turn so the button is untouched while the browser builds
    // the form data set. The button carries no name, so it contributes
    // nothing either way, but the ordering costs nothing to get right.
    window.setTimeout(lock, 0);
  });

  // Coming back through history restores the document as it was left, locked
  // button and all, so a second attempt would find a dead control.
  window.addEventListener('pageshow', function (event) {
    if (event.persisted) unlock();
  });
})();
